import {DataSource} from '@angular/cdk/table';
import {CollectionViewer, ListRange} from '@angular/cdk/collections';
import {BehaviorSubject, debounceTime, from, Observable, Subject, Subscription, switchMap} from 'rxjs';
import {OptionsService} from '../core/options.service';
import {SearchParams} from '../core/search-params';
import {Option} from '../core/definitions/option-info';

export const QUERY_DEBOUNCE_MS = 500;
// Must match 'rows' in OptionsService.getSearchParams: a shorter page is taken as the last one.
export const PAGE_SIZE = 200;

export class VirtualScrollEditDataSourceList implements DataSource<Option> {
  private readonly dataStream = new BehaviorSubject([]);
  private readonly queryStream = new Subject<String>();
  private readonly subscription = new Subscription();

  private loading = true;
  private generation = 0;
  private complete: boolean;
  private pageLoading: boolean;

  constructor(
    private optionsService: OptionsService,
    private params: SearchParams,
  ) {
    this.subscription.add(this.queryStream.pipe(
      debounceTime(QUERY_DEBOUNCE_MS),
      switchMap(query => from(this.loadFirstPage(query)))
    ).subscribe());
    this.loadFirstPage(this.params.query).then(() => this.loading = false);
  }

  /**
   * Triggers when we open the view
   * @param collectionViewer
   */
  connect(collectionViewer: CollectionViewer): Observable<Option[]> {
    this.subscription.add(collectionViewer.viewChange.subscribe(range => {
      if (this.needsNextPage(range)) {
        this.loadNextPage().then();
      }
    }));
    return this.dataStream;
  }

  /**
   * Triggers when we close the view
   * @param collectionViewer
   */
  disconnect(collectionViewer: CollectionViewer): void {}

  /**
   * Searches for the query once no new query has arrived for QUERY_DEBOUNCE_MS.
   * Results of an earlier query that arrive later are dropped.
   * @param query string value of the desired query
   */
  searchByQuery(query: String) {
    this.queryStream.next(query);
  }

  /**
   * Method return the value corresponding to the given index
   * @param selectedRow index
   */
  getIndex(selectedRow: number) {
    return this.dataStream.value[selectedRow];
  }

  /**
   * Call this method to unsubscribe all subscriptions.
   * We use this method instead of disconnect, because disconnect triggers when we click on the input, tries to
   * search , click "hide" or outside the select.
   */
  unsubscribe() {
    this.subscription.unsubscribe();
  }

  /**
   * @return dataStream as Observable()
   */
  asObservable(): Observable<any[]> {
    return this.dataStream.asObservable();
  }

  isLoading(): Boolean {
    return this.loading;
  }

  private async loadFirstPage(query: String) {
    const generation = ++this.generation;
    this.pageLoading = true;
    try {
      const options = await this.optionsService.searchOptions({...this.params, query});
      if (generation === this.generation) {
        this.params.query = query;
        this.complete = options.length < PAGE_SIZE;
        this.dataStream.next(options);
      }
    } catch {
      // CmsApiService has already shown the error; the previous options and query stay in use.
    } finally {
      if (generation === this.generation) {
        this.pageLoading = false;
      }
    }
  }

  private needsNextPage(range: ListRange): boolean {
    return !this.complete && !this.pageLoading && range.end >= this.dataStream.value.length;
  }

  private async loadNextPage() {
    const generation = this.generation;
    const loaded: Option[] = this.dataStream.value;
    this.pageLoading = true;
    try {
      const range = {start: loaded.length, end: loaded.length + PAGE_SIZE};
      const options = await this.optionsService.searchOptions(this.params, range, [...loaded]);
      if (generation === this.generation) {
        this.complete = options.length - loaded.length < PAGE_SIZE;
        this.dataStream.next(options);
      }
    } catch {
      // CmsApiService has already shown the error; scrolling to the end again retries the page.
    } finally {
      if (generation === this.generation) {
        this.pageLoading = false;
      }
    }
  }
}
